import { HelpCircle, Package, CreditCard, Truck, RefreshCw, Shield, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

export default function FAQ() {
  const categories = [
    {
      name: 'Orders & Payments',
      icon: CreditCard,
      faqs: [
        {
          q: 'How do I place an order?',
          a: 'Browse our products, add items to your cart, and proceed to checkout. Fill in your shipping details and complete payment through our secure Square checkout. You\'ll receive a confirmation email once your order is placed.'
        },
        {
          q: 'What payment methods do you accept?',
          a: 'We accept all major credit and debit cards (Visa, MasterCard, American Express, Discover) through our secure Square payment system. We also support Apple Pay and Google Pay.'
        },
        {
          q: 'Can I modify or cancel my order?',
          a: 'Orders can be modified or cancelled within 1 hour of placement. After that, the order enters processing and cannot be changed. Please contact our support team immediately if you need to make changes.'
        },
        {
          q: 'Is my payment information secure?',
          a: 'Absolutely! We use Square for payment processing, which is PCI DSS compliant. Your payment information is encrypted and never stored on our servers.'
        }
      ]
    },
    {
      name: 'Shipping & Delivery',
      icon: Truck,
      faqs: [
        {
          q: 'How long does shipping take?',
          a: 'Standard shipping takes 3-7 business days. Expedited shipping (2-3 business days) is available at checkout for an additional fee. Processing time is typically 1-2 business days.'
        },
        {
          q: 'Do you offer free shipping?',
          a: 'Yes! We offer free standard shipping on all orders over $50 within the continental United States.'
        },
        {
          q: 'How can I track my order?',
          a: 'Once your order ships, you\'ll receive an email with tracking information. You can also track your order by entering your email in the "My Orders" section of our website.'
        },
        {
          q: 'Do you ship internationally?',
          a: 'Currently, we only ship within the United States. We\'re working on expanding our shipping to international destinations soon!'
        }
      ]
    },
    {
      name: 'Returns & Refunds',
      icon: RefreshCw,
      faqs: [
        {
          q: 'What is your return policy?',
          a: 'We accept returns within 30 days of delivery for unused items in original packaging. Some items like electronics may have specific return conditions. Please check the product page for details.'
        },
        {
          q: 'How do I initiate a return?',
          a: 'Contact our customer support team with your order number and reason for return. We\'ll provide you with a prepaid return label and instructions. Once we receive and inspect the item, your refund will be processed.'
        },
        {
          q: 'How long does it take to process a refund?',
          a: 'Refunds are processed within 5-7 business days after we receive your returned item. The refund will be credited to your original payment method.'
        },
        {
          q: 'What if I receive a damaged or defective item?',
          a: 'Please contact us immediately with photos of the damage. We\'ll arrange a free return and send you a replacement or full refund at no additional cost.'
        }
      ]
    },
    {
      name: 'Account & Security',
      icon: Shield,
      faqs: [
        {
          q: 'Do I need an account to place an order?',
          a: 'No, we offer guest checkout for your convenience. However, creating an account allows you to track orders, save favorites, and enjoy a faster checkout experience.'
        },
        {
          q: 'How do I track my orders without an account?',
          a: 'Simply visit the "My Orders" page and enter the email address you used during checkout. You\'ll be able to see all orders associated with that email.'
        },
        {
          q: 'How is my personal information protected?',
          a: 'We use industry-standard encryption and security measures to protect your data. We never sell your information to third parties. Please review our Privacy Policy for more details.'
        }
      ]
    },
    {
      name: 'Products & Stock',
      icon: Package,
      faqs: [
        {
          q: 'Are all products brand new?',
          a: 'Yes, all products sold on IPVALUE MANAGEMENTS are brand new and come with manufacturer warranty where applicable.'
        },
        {
          q: 'What if an item is out of stock?',
          a: 'If an item is out of stock, you can sign up for email notifications to be alerted when it\'s back. We regularly restock popular items.'
        },
        {
          q: 'Do you price match?',
          a: 'We strive to offer the best prices. If you find a lower price from an authorized retailer, contact us and we\'ll do our best to match it.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="faq-page">
      {/* Hero */}
      <section className="bg-[#003366] py-16">
        <div className="max-w-[1440px] mx-auto px-4 text-center">
          <HelpCircle size={48} className="mx-auto text-[#FFD700] mb-4" />
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Help Center</h1>
          <p className="text-slate-300 max-w-xl mx-auto">
            Find answers to frequently asked questions about orders, shipping, returns, and more.
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {categories.map((category, idx) => (
          <div key={idx} className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#003366] rounded-lg flex items-center justify-center">
                <category.icon className="text-[#FFD700]" size={20} />
              </div>
              <h2 className="text-xl font-bold text-slate-900">{category.name}</h2>
            </div>
            
            <Accordion type="single" collapsible className="bg-white rounded-lg border">
              {category.faqs.map((faq, i) => (
                <AccordionItem key={i} value={`${idx}-${i}`}>
                  <AccordionTrigger className="px-6 hover:no-underline hover:bg-slate-50">
                    <span className="text-left font-medium">{faq.q}</span>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-slate-600">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ))}

        {/* Contact CTA */}
        <div className="bg-[#003366] rounded-xl p-8 text-center text-white mt-12">
          <h3 className="text-xl font-bold mb-2">Still have questions?</h3>
          <p className="text-slate-300 mb-6">
            Our customer support team is here to help you with any questions or concerns.
          </p>
          <Link to="/contact">
            <Button className="bg-[#FFD700] hover:bg-[#F4C430] text-slate-900 font-semibold">
              <Mail size={18} className="mr-2" />
              Contact Support
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
